<template>
  <page-header-wrapper>
    <div class="purchase-order-page erp-list-page baidao-list-page">
      <section class="erp-search-card purchase-search">
        <div class="search-fields">
          <a-input v-model.trim="query.orderNo" allow-clear placeholder="采购订单" @pressEnter="loadData" />
          <a-range-picker v-model="dateRange" value-format="YYYY-MM-DD" style="width:100%" />
          <a-select v-model="query.auditStatus" allow-clear placeholder="审核状态">
            <a-select-option value="unaudited">未审核</a-select-option>
            <a-select-option value="audited">已审核</a-select-option>
          </a-select>
          <a-input v-model.trim="query.supplierCode" allow-clear placeholder="供应商编号/名称" @pressEnter="loadData" />
        </div>
        <div class="search-actions">
          <a-button type="primary" @click="search">搜索</a-button>
          <a-button @click="resetQuery">重置</a-button>
        </div>
      </section>

      <section class="erp-table-card">
        <header class="erp-table-toolbar">
          <div><h3>采购订单</h3><p>先审核采购订单，再生成采购入库；数量可为负数（红字/退补），分批入库不允许超收。</p></div>
          <div class="toolbar-actions">
            <a-button type="primary" icon="plus" @click="openCreate">新增采购订单</a-button>
            <a-button icon="download" :loading="exporting" @click="doExport">导出</a-button>
          </div>
        </header>
        <a-table
          :columns="columns"
          :data-source="rows"
          :loading="loading"
          row-key="id"
          bordered
          size="middle"
          :pagination="pagination"
          :scroll="{ x: 1860 }"
          :locale="{ emptyText: '暂无采购订单' }"
          @change="onTableChange"
        >
          <template slot="status" slot-scope="value">
            <a-tag :color="value === 'audited' ? 'green' : 'orange'">{{ value === 'audited' ? '已审核' : '未审核' }}</a-tag>
          </template>
          <template slot="money" slot-scope="value"><span class="money">¥ {{ money(value) }}</span></template>
          <template slot="emptyText" slot-scope="value">{{ value || '—' }}</template>
          <template slot="delivery" slot-scope="value">{{ value ? String(value).slice(0, 10) : '—' }}</template>
          <template slot="operation" slot-scope="text, record">
            <div class="row-actions">
              <a @click="openView(record)">查看</a>
              <a v-if="record.auditStatus === 'unaudited'" @click="openEdit(record)">编辑</a>
              <a v-if="record.auditStatus === 'unaudited'" @click="confirmAudit(record)">审核</a>
              <a v-if="record.auditStatus === 'audited' && !record.hasAuditedInbound" @click="confirmReverse(record)">反审核</a>
              <a v-if="canInbound(record)" @click="goInbound(record)">新增采购入库单</a>
              <a v-if="record.auditStatus === 'unaudited' && !record.hasAuditedInbound" class="erp-danger-link" @click="confirmDelete(record)">删除</a>
            </div>
          </template>
        </a-table>
      </section>

      <a-drawer
        :width="drawerWidth"
        :visible="drawerVisible"
        :closable="false"
        :mask-closable="false"
        :body-style="{ padding: 0 }"
        class="erp-doc-drawer"
        @close="closeDrawer"
      >
        <div class="erp-drawer-shell">
          <div class="erp-drawer-header">
            <div>
              <button class="erp-back-button" type="button" @click="closeDrawer"><a-icon type="arrow-left" /></button>
              <span class="erp-drawer-kicker">{{ readOnly ? '查看采购订单' : (form.id ? '编辑采购订单' : '新增采购订单') }}</span>
              <h3>{{ form.orderNo || '新增采购订单' }}</h3>
            </div>
            <a-tag :color="form.auditStatus === 'audited' ? 'green' : 'orange'">{{ form.auditStatus === 'audited' ? '已审核' : '未审核' }}</a-tag>
          </div>

          <a-form-model ref="documentForm" :model="form" :rules="rules" layout="vertical" class="document-body erp-drawer-content">
            <section class="erp-form-card">
              <div class="erp-section-title"><span class="erp-section-index">01</span><div><h4>基础信息</h4><p>先选供应商，再选该供应商在商品档案中已维护的收货仓。</p></div></div>
              <div class="document-grid">
                <a-form-model-item label="订单编号"><a-input :value="form.orderNo || '系统自动生成'" disabled /></a-form-model-item>
                <a-form-model-item label="单据日期" prop="orderDate"><a-date-picker v-model="form.orderDate" value-format="YYYY-MM-DD" :disabled="readOnly" placeholder="年/月/日" style="width:100%" /></a-form-model-item>
                <a-form-model-item label="客商订单号"><a-input v-model.trim="form.partnerOrderNo" :disabled="readOnly" :max-length="64" placeholder="请输入" /></a-form-model-item>
                <a-form-model-item label="供应商编号"><a-input :value="form.supplierCode || '选择供应商带出'" disabled /></a-form-model-item>
                <a-form-model-item label="供应商名称" prop="supplierId">
                  <a-select v-model="form.supplierId" show-search option-filter-prop="children" :disabled="readOnly" placeholder="输入编号或名称" style="width:100%" @change="onSupplierChange">
                    <a-select-option v-for="item in suppliers" :key="item.id" :value="String(item.id)">{{ item.supplierCode }} · {{ item.supplierName }}</a-select-option>
                  </a-select>
                </a-form-model-item>
                <a-form-model-item label="收货仓库" prop="warehouseId">
                  <a-select v-model="form.warehouseId" show-search option-filter-prop="children" :disabled="readOnly || !form.supplierId" placeholder="请选择仓库" style="width:100%" @change="onWarehouseChange">
                    <a-select-option v-for="item in supplierWarehouses" :key="item.id" :value="item.id">{{ item.warehouseName }}</a-select-option>
                  </a-select>
                </a-form-model-item>
                <a-form-model-item label="交货时间"><a-date-picker v-model="form.deliveryTime" value-format="YYYY-MM-DD HH:mm:ss" show-time :disabled="readOnly" placeholder="请选择" style="width:100%" /></a-form-model-item>
                <a-form-model-item label="交货地址"><a-input v-model.trim="form.deliveryAddress" :disabled="readOnly" :max-length="255" placeholder="请输入" /></a-form-model-item>
                <a-form-model-item label="业务员">
                  <a-select v-model="form.purchaseUserId" show-search option-filter-prop="children" :disabled="readOnly" placeholder="默认当前创建人" style="width:100%">
                    <a-select-option v-for="item in users" :key="item.userId" :value="item.userId">{{ item.nickName || item.userName }}</a-select-option>
                  </a-select>
                </a-form-model-item>
                <a-form-model-item label="业务部门"><a-input :value="form.deptName || '默认当前创建人部门'" disabled /></a-form-model-item>
                <a-form-model-item label="备注"><a-input v-model.trim="form.remark" :disabled="readOnly" :max-length="500" placeholder="请输入" /></a-form-model-item>
                <a-form-model-item label="附件">
                  <file-upload :value="form.attachmentUrl || ''" type="file" field-name="attachmentUrl" :count="3" :disabled="readOnly" @change="attachmentChanged" />
                </a-form-model-item>
              </div>
            </section>

            <section class="erp-form-card">
              <div class="erp-section-heading-row">
                <div class="erp-section-title"><span class="erp-section-index">02</span><div><h4>商品明细</h4><p>可选基本单位或整箱单位，数量与含税单价均按所选单位录入；保存后数量会按箱规换算为基本单位。新增行默认带出该规格最近一次采购的单位与含税单价，无历史时用基本单位与档案供应成本。税率取商品（SPU）档案，不可改。</p></div></div>
                <div v-if="!readOnly" class="detail-toolbar-actions">
                  <a-button type="primary" ghost icon="plus" :disabled="!form.supplierId || !form.warehouseId" @click="openProductPicker">选择商品</a-button>
                  <a-button icon="upload" :disabled="!form.supplierId || !form.warehouseId" @click="openItemImport">导入明细</a-button>
                </div>
              </div>
              <div class="detail-table-wrap erp-line-table-wrap">
                <table class="detail-table erp-line-table">
                  <thead>
                    <tr>
                      <th style="width:56px">序号</th>
                      <th style="width:140px">商品编码</th>
                      <th style="min-width:160px">商品名称</th>
                      <th style="width:140px">规格</th>
                      <th style="width:100px">单位</th>
                      <th style="width:120px">数量</th>
                      <th style="width:120px">含税单价(元)</th>
                      <th style="width:90px">税率%</th>
                      <th style="width:120px">含税总金额</th>
                      <th v-if="!readOnly" style="width:70px">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in form.items" :key="item.clientKey || item.id || index">
                      <td>{{ index + 1 }}</td>
                      <td>{{ item.skuCode || '—' }}</td>
                      <td>{{ item.spuName || item.skuName || '—' }}</td>
                      <td>{{ item.skuName || item.specName || '—' }}</td>
                      <td>
                        <a-select
                          v-if="!readOnly && purchaseUnitChoices(item).length > 1"
                          :value="item.unitId != null ? String(item.unitId) : undefined"
                          style="width:100%"
                          @change="v => onUnitChange(item, v)"
                        >
                          <a-select-option v-for="unit in purchaseUnitChoices(item)" :key="String(unit.unitId)" :value="String(unit.unitId)">{{ unit.unitName }}</a-select-option>
                        </a-select>
                        <span v-else>{{ item.unitName || '—' }}</span>
                      </td>
                      <td><a-input-number v-model="item.qtyOrder" :disabled="readOnly" :precision="3" style="width:100%" /></td>
                      <td><a-input-number v-model="item.purchasePrice" :disabled="readOnly" :min="0" :precision="2" style="width:100%" /></td>
                      <td>{{ Number(item.taxRate || 0) }}%</td>
                      <td class="money">¥ {{ lineAmount(item) }}</td>
                      <td v-if="!readOnly"><a class="erp-danger-link" @click="removeLine(index)">删除</a></td>
                    </tr>
                    <tr v-if="!form.items.length"><td :colspan="readOnly ? 9 : 10" class="empty-row">请先选择供应商和收货仓库，再选择商品规格</td></tr>
                  </tbody>
                </table>
              </div>
              <div class="amount-summary">
                <span>无税金额 ¥ {{ amountSummary.amount }}</span>
                <span>税额 ¥ {{ amountSummary.tax }}</span>
                <b>价税合计 ¥ {{ amountSummary.total }}</b>
              </div>
            </section>
          </a-form-model>

          <a-modal
            title="选择商品规格"
            :visible="productPicker.visible"
            width="1080px"
            ok-text="添加到订单"
            cancel-text="取消"
            :confirm-loading="productPicker.loading"
            @ok="confirmProductPicker"
            @cancel="productPicker.visible = false"
          >
            <div class="picker-bar">
              <a-input v-model.trim="productPicker.keyword" allow-clear placeholder="商品名称、编码或规格" @pressEnter="loadSkuOptions" />
              <a-button type="primary" @click="loadSkuOptions">查询</a-button>
              <span>当前范围 {{ productPicker.list.length }} 个规格，已在订单中的规格不可取消</span>
            </div>
            <a-table
              row-key="skuId"
              size="small"
              bordered
              :columns="pickerColumns"
              :data-source="productPicker.list"
              :loading="productPicker.loading"
              :pagination="false"
              :row-selection="productRowSelection"
              :scroll="{ y: 420 }"
              :locale="{ emptyText: '没有可采购规格：① 仓库信息中主/备选供应商须与当前订单供应商一致；② SPU 与每个 SKU 规格都须为「启用」（仅启用 SPU、规格仍停用时不出现）' }"
            />
          </a-modal>

          <a-modal
            title="导入采购明细"
            :visible="itemImport.visible"
            :width="itemImport.result && itemImport.result.failureCount ? 920 : 680"
            :mask-closable="false"
            :closable="!itemImport.importing"
            class="po-item-import-modal"
            @cancel="closeItemImport"
          >
            <a-alert
              type="info"
              show-icon
              message="按当前单据的供应商与收货仓校验规格"
              description="导入成功行将追加到下方明细表，不会自动保存订单。规格名称须与档案一致且在该供应商+仓库可采购；含税单价留空时与「选择商品」默认定价规则一致。"
              style="margin-bottom:14px"
            />
            <div class="po-import-template-row">
              <div><b>采购明细导入模板</b><p>表头：规格名称、数量、含税单价（选填）；单位按该规格默认采购单位带出。最多 1000 行。</p></div>
              <a-button icon="download" @click="downloadItemImportTemplate">下载模板</a-button>
            </div>
            <a-upload-dragger
              accept=".xlsx,.xls"
              :multiple="false"
              :file-list="itemImport.fileList"
              :disabled="itemImport.importing"
              :before-upload="beforeItemImport"
              :remove="removeItemImportFile"
            >
              <p class="ant-upload-drag-icon"><a-icon type="inbox" /></p>
              <p class="ant-upload-text">点击选择或拖入 Excel 文件</p>
              <p class="ant-upload-hint">支持 .xlsx、.xls</p>
            </a-upload-dragger>
            <section v-if="itemImport.result" class="po-import-result" :class="{ 'has-errors': itemImport.result.failureCount }">
              <div class="po-import-result-summary">
                <a-icon :type="itemImport.result.failureCount ? 'exclamation-circle' : 'check-circle'" />
                <div>
                  <b>已处理 {{ itemImport.result.totalCount }} 行</b>
                  <p>成功 {{ itemImport.result.successCount }} 行，失败 {{ itemImport.result.failureCount }} 行</p>
                </div>
              </div>
              <div v-if="itemImport.result.failureCount" class="po-import-error-table-wrap">
                <h4 class="po-import-error-title">失败明细</h4>
                <a-table
                  row-key="rowNumber"
                  size="small"
                  bordered
                  :pagination="{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }"
                  :columns="itemImportErrorColumns"
                  :data-source="itemImport.result.errorRows || []"
                  :scroll="{ x: 860, y: 280 }"
                />
              </div>
            </section>
            <template slot="footer">
              <a-button :disabled="itemImport.importing" @click="closeItemImport">{{ itemImport.result ? '完成' : '取消' }}</a-button>
              <a-button type="primary" :loading="itemImport.importing" :disabled="!itemImport.fileList.length" @click="submitItemImport">开始导入</a-button>
            </template>
          </a-modal>

          <div v-if="drawerVisible" class="erp-drawer-footer">
            <div><a-icon type="info-circle" /> 审核后才可采购入库；有入库单后不可再编辑、反审核或删除。</div>
            <div>
              <a-button @click="closeDrawer">取消</a-button>
              <a-button v-if="!readOnly" type="primary" :loading="saving" @click="submit">确定</a-button>
            </div>
          </div>
        </div>
      </a-drawer>
    </div>
  </page-header-wrapper>
</template>

<script>
import moment from 'moment'
import { listSupplier } from '@/api/erp/supplier'
import { pageUser } from '@/api/system/user'
import {
  pagePurchaseOrder, getPurchaseOrder, savePurchaseOrder, auditPurchaseOrder,
  reverseAuditPurchaseOrder, deletePurchaseOrder, exportPurchaseOrder,
  getPurchaseOrderCreator, listPurchaseOrderWarehouses, listPurchaseOrderSku, listPurchaseOrderLastPrices,
  importPurchaseOrderItems
} from '@/api/erp/purchaseOrder'

let lineSeed = 0
const emptyForm = () => ({
  id: undefined, orderNo: '', orderDate: moment().format('YYYY-MM-DD'),
  supplierId: undefined, supplierCode: '', supplierName: '',
  warehouseId: undefined, purchaseUserId: undefined, deptName: '',
  deliveryTime: undefined, partnerOrderNo: '', deliveryAddress: '',
  attachmentUrl: '', remark: '', auditStatus: 'unaudited', items: []
})

export default {
  name: 'ErpPurchaseOrder',
  data () {
    return {
      viewportWidth: window.innerWidth,
      loading: false,
      saving: false,
      exporting: false,
      drawerVisible: false,
      readOnly: false,
      query: {},
      dateRange: [],
      rows: [],
      filling: false,
      suppliers: [],
      supplierWarehouses: [],
      users: [],
      form: emptyForm(),
      productPicker: { visible: false, loading: false, keyword: '', list: [], selectedRowKeys: [] },
      itemImport: { visible: false, importing: false, fileList: [], result: null },
      itemImportErrorColumns: [
        { title: '行号', dataIndex: 'rowNumber', width: 64, align: 'center' },
        { title: '规格名称', dataIndex: 'skuName', width: 200, ellipsis: true },
        { title: '数量', dataIndex: 'qtyOrder', width: 88 },
        { title: '含税单价', dataIndex: 'purchasePrice', width: 96 },
        { title: '失败原因', dataIndex: 'errorMessage', ellipsis: true }
      ],
      pickerColumns: [
        { title: '商品编码', dataIndex: 'spuCode', width: 140 },
        { title: '商品名称', dataIndex: 'spuName', width: 180 },
        { title: '规格编码', dataIndex: 'skuCode', width: 140 },
        { title: '规格', key: 'specDisplay', width: 160, customRender: (text, row) => (row.skuName || row.specName || '—') },
        { title: '商品税率%', dataIndex: 'taxRate', width: 110 },
        { title: '供应成本', dataIndex: 'supplyCost', width: 110 }
      ],
      pagination: { current: 1, pageSize: 10, total: 0, showSizeChanger: true, showQuickJumper: true },
      rules: {
        orderDate: [{ required: true, message: '请选择单据日期', trigger: 'change' }],
        supplierId: [{ required: true, message: '请选择供应商', trigger: 'change' }],
        warehouseId: [{ required: true, message: '请选择收货仓库', trigger: 'change' }]
      },
      columns: [
        { title: '采购订单', dataIndex: 'orderNo', width: 180 },
        { title: '单据日期', dataIndex: 'orderDate', width: 110 },
        { title: '审核状态', dataIndex: 'auditStatus', width: 90, scopedSlots: { customRender: 'status' } },
        { title: '供应商编号', dataIndex: 'supplierCode', width: 140 },
        { title: '供应商名称', dataIndex: 'supplierName', width: 160 },
        { title: '预计到货日期', dataIndex: 'deliveryTime', width: 120, scopedSlots: { customRender: 'delivery' } },
        { title: '价税合计', dataIndex: 'totalTaxAmount', width: 110, align: 'right', scopedSlots: { customRender: 'money' } },
        { title: '无税金额', dataIndex: 'totalAmount', width: 110, align: 'right', scopedSlots: { customRender: 'money' } },
        { title: '客商订单号', dataIndex: 'partnerOrderNo', width: 140, scopedSlots: { customRender: 'emptyText' } },
        { title: '收货仓', dataIndex: 'warehouseName', width: 130 },
        { title: '备注', dataIndex: 'remark', width: 140, ellipsis: true, scopedSlots: { customRender: 'emptyText' } },
        { title: '业务员', dataIndex: 'purchaseUserName', width: 100, scopedSlots: { customRender: 'emptyText' } },
        { title: '部门名称', dataIndex: 'deptName', width: 120, scopedSlots: { customRender: 'emptyText' } },
        { title: '操作', key: 'operation', width: 260, fixed: 'right', scopedSlots: { customRender: 'operation' } }
      ]
    }
  },
  computed: {
    drawerWidth () { return this.viewportWidth < 1100 ? '100%' : '88%' },
    productRowSelection () {
      return {
        selectedRowKeys: this.productPicker.selectedRowKeys,
        onChange: keys => {
          const locked = this.form.items.map(item => item.skuId).filter(Boolean)
          const merged = []
          locked.concat(keys).forEach(id => {
            if (!merged.some(item => String(item) === String(id))) merged.push(id)
          })
          this.productPicker.selectedRowKeys = merged
        },
        getCheckboxProps: record => ({
          props: { disabled: this.form.items.some(item => String(item.skuId) === String(record.skuId)) }
        })
      }
    },
    amountSummary () {
      let total = 0
      let amount = 0
      let tax = 0
      this.form.items.forEach(item => {
        const lineTotal = Number(item.qtyOrder || 0) * Number(item.purchasePrice || 0)
        const rate = Number(item.taxRate || 0)
        const lineAmount = rate ? lineTotal / (1 + rate / 100) : lineTotal
        total += lineTotal
        amount += lineAmount
        tax += lineTotal - lineAmount
      })
      return { total: total.toFixed(2), amount: amount.toFixed(2), tax: tax.toFixed(2) }
    }
  },
  created () {
    this.loadBaseData()
    this.loadData()
    if (this.$route.query.openId) {
      this.openDocument(Number(this.$route.query.openId), this.$route.query.mode === 'view')
    }
  },
  mounted () { window.addEventListener('resize', this.onResize) },
  beforeDestroy () { window.removeEventListener('resize', this.onResize) },
  methods: {
    onResize () { this.viewportWidth = window.innerWidth },
    loadBaseData () {
      Promise.all([
        listSupplier(true),
        pageUser({ pageNum: 1, pageSize: 200, status: 1 })
      ]).then(([s, u]) => {
        this.suppliers = s.data || []
        const userPage = u.data || {}
        this.users = Array.isArray(userPage) ? userPage : (userPage.records || userPage.rows || [])
      }).catch(() => {
        this.suppliers = this.suppliers || []
        this.users = []
      })
    },
    loadData () {
      this.loading = true
      const params = {
        ...this.query,
        current: this.pagination.current,
        size: this.pagination.pageSize,
        dateStart: this.dateRange[0],
        dateEnd: this.dateRange[1]
      }
      pagePurchaseOrder(params).then(res => {
        const page = res.data || {}
        this.rows = page.records || []
        this.pagination.total = Number(page.total || 0)
      }).finally(() => { this.loading = false })
    },
    search () { this.pagination.current = 1; this.loadData() },
    resetQuery () { this.query = {}; this.dateRange = []; this.pagination.current = 1; this.loadData() },
    onTableChange (page) { this.pagination.current = page.current; this.pagination.pageSize = page.pageSize; this.loadData() },
    canInbound (record) {
      // hasAvailableInbound：订单剩余已扣「未审核入库单」占用后仍大于 0
      if (record.auditStatus !== 'audited') return false
      if (record.hasAvailableInbound === false || record.hasAvailableInbound === 0) return false
      if (record.hasAvailableInbound === true || record.hasAvailableInbound === 1) return true
      return record.executeStatus !== 'completed'
    },
    openCreate () {
      this.form = emptyForm()
      this.supplierWarehouses = []
      this.readOnly = false
      this.drawerVisible = true
      getPurchaseOrderCreator().then(res => {
        const data = res.data || {}
        if (this.form.id || !this.drawerVisible) return
        this.form.purchaseUserId = data.userId
        this.form.deptName = data.deptName || ''
        this.ensureUserOption(data)
      })
    },
    ensureUserOption (profile) {
      if (!profile || !profile.userId) return
      if (this.users.some(item => String(item.userId) === String(profile.userId))) return
      this.users = this.users.concat([{ userId: profile.userId, nickName: profile.userName || '当前用户' }])
    },
    openEdit (record) { this.openDocument(record.id, false) },
    openView (record) { this.openDocument(record.id, true) },
    openDocument (id, readOnly) {
      getPurchaseOrder(id).then(res => {
        const data = res.data || {}
        this.filling = true
        this.form = {
          ...emptyForm(),
          ...data,
          deliveryTime: data.deliveryTime || undefined,
          items: (data.items || []).map(item => {
            const row = {
              ...item,
              unitId: this.idStr(item.unitId),
              purchaseUnits: this.normalizePurchaseUnits(item.purchaseUnits || []),
              clientKey: `po-line-${item.id}`,
              convertRateToBase: Number(item.convertRateToBase) > 0 ? Number(item.convertRateToBase) : 1
            }
            return row
          })
        }
        this.readOnly = readOnly || data.auditStatus === 'audited' || !!data.hasAuditedInbound
        this.drawerVisible = true
        this.loadSupplierWarehouses(data.supplierId, true).finally(() => { this.filling = false })
      })
    },
    closeDrawer () { if (!this.saving) this.drawerVisible = false },
    attachmentChanged (value) {
      this.form.attachmentUrl = typeof value === 'string' ? value : (value && (value.attachmentUrl || value.path)) || ''
    },
    onSupplierChange (supplierId) {
      if (this.filling) return
      const supplier = this.suppliers.find(item => String(item.id) === String(supplierId))
      this.form.supplierCode = supplier ? supplier.supplierCode : ''
      this.form.supplierName = supplier ? supplier.supplierName : ''
      this.form.warehouseId = undefined
      this.form.items = []
      this.supplierWarehouses = []
      if (supplierId) this.loadSupplierWarehouses(supplierId, false)
    },
    onWarehouseChange () {
      if (this.filling) return
      this.form.items = []
    },
    loadSupplierWarehouses (supplierId, keepItems) {
      if (!supplierId) return Promise.resolve()
      return listPurchaseOrderWarehouses(supplierId).then(res => {
        this.supplierWarehouses = res.data || []
        if (keepItems && this.form.warehouseId) this.mergeLineUnits()
      })
    },
    mergeLineUnits () {
      if (!this.form.supplierId || !this.form.warehouseId || !this.form.items.length) return
      listPurchaseOrderSku(this.form.supplierId, this.form.warehouseId).then(res => {
        const map = {}
        ;(res.data || []).forEach(sku => { map[String(sku.skuId)] = sku })
        this.form.items.forEach(item => {
          const sku = map[String(item.skuId)]
          if (!sku) return
          this.$set(item, 'purchaseUnits', this.normalizePurchaseUnits(sku.purchaseUnits || []))
          if (item.taxRate == null) item.taxRate = sku.taxRate
          item.skuName = sku.skuName || item.skuName
          item.specName = sku.skuName || sku.specName || item.specName
          if (!item.spuName) item.spuName = sku.spuName
          if (item.unitId) {
            const unit = (sku.purchaseUnits || []).find(u => String(u.unitId) === String(item.unitId))
            if (unit) item.convertRateToBase = this.unitConvertRate(unit)
          }
        })
      })
    },
    removeLine (index) { this.form.items.splice(index, 1) },
    purchaseUnits (line) { return line.purchaseUnits || [] },
    idStr (value) {
      if (value == null || value === '') return ''
      return String(value)
    },
    normalizePurchaseUnit (unit) {
      if (!unit) return unit
      return {
        ...unit,
        unitId: this.idStr(unit.unitId)
      }
    },
    normalizePurchaseUnits (units) {
      const list = (units || []).map(u => this.normalizePurchaseUnit(u)).filter(u => u && u.unitId)
      const byKey = new Map()
      list.forEach(u => {
        const key = `${u.unitName}|${this.unitConvertRate(u)}`
        if (!byKey.has(key)) byKey.set(key, u)
      })
      return Array.from(byKey.values()).sort((a, b) => this.unitConvertRate(a) - this.unitConvertRate(b))
    },
    /** 基本单位 + 整箱；按单位名+换算率去重（避免雪花 ID 精度问题出现两个「袋」） */
    purchaseUnitChoices (line) {
      return this.normalizePurchaseUnits(this.purchaseUnits(line))
    },
    unitConvertRate (unit) {
      const rate = Number(unit && unit.convertRateToBase)
      return rate > 0 ? rate : 1
    },
    pickBasePurchaseUnit (units) {
      if (!units || !units.length) return null
      const base = units.find(u => this.unitConvertRate(u) === 1)
      return base || units[0]
    },
    pickDefaultPurchaseUnit (sku, units) {
      if (!units || !units.length) return null
      const lastId = sku && sku.lastPurchaseUnitId
      if (lastId != null) {
        const matched = units.find(u => String(u.unitId) === String(lastId))
        if (matched) return matched
      }
      return this.pickBasePurchaseUnit(units)
    },
    roundPurchasePrice (value) {
      const n = Number(value || 0)
      if (!Number.isFinite(n)) return 0
      return Math.round(n * 100) / 100
    },
    buildLastPriceByUnitId (sku) {
      const map = {}
      ;(sku.lastPurchasePrices || []).forEach(row => {
        if (row.unitId != null && row.purchasePrice != null) {
          map[String(row.unitId)] = Number(row.purchasePrice)
        }
      })
      return map
    },
    resolvePurchasePrice (sku, unitId, lastPriceByUnitId) {
      const uid = unitId != null ? String(unitId) : ''
      if (lastPriceByUnitId && lastPriceByUnitId[uid] != null) {
        return this.roundPurchasePrice(lastPriceByUnitId[uid])
      }
      const baseSupply = sku.supplyCost == null ? 0 : Number(sku.supplyCost)
      return this.roundPurchasePrice(baseSupply)
    },
    /** 默认单位 + 按单位最近价/supplyCost，与后端 lastPurchase* 字段一致 */
    resolveDefaultPurchaseLine (sku, unit, lastPriceByUnitId) {
      const unitId = unit ? unit.unitId : undefined
      return this.resolvePurchasePrice(sku, unitId, lastPriceByUnitId)
    },
    syncLineUnit (line, unitId, { convertPrice = false } = {}) {
      const units = this.purchaseUnitChoices(line)
      const unit = units.find(item => String(item.unitId) === String(unitId))
      if (!unit) return
      if (convertPrice) {
        const oldRate = Number(line.convertRateToBase) > 0 ? Number(line.convertRateToBase) : 1
        const newRate = this.unitConvertRate(unit)
        if (oldRate !== newRate) {
          line.purchasePrice = this.roundPurchasePrice(Number(line.purchasePrice || 0) * (newRate / oldRate))
        }
      }
      line.unitId = this.idStr(unit.unitId)
      line.unitName = unit.unitName
      line.convertRateToBase = this.unitConvertRate(unit)
    },
    openItemImport () {
      if (!this.form.supplierId || !this.form.warehouseId) {
        this.$message.warning('请先选择供应商和收货仓库')
        return
      }
      this.itemImport.fileList = []
      this.itemImport.result = null
      this.itemImport.visible = true
    },
    closeItemImport () {
      if (this.itemImport.importing) return
      this.itemImport.visible = false
      this.itemImport.fileList = []
      this.itemImport.result = null
    },
    downloadItemImportTemplate () {
      this.download('erp/purchase-order/item-import-template', {}, `采购订单明细导入模板_${new Date().getTime()}.xlsx`)
    },
    beforeItemImport (file) {
      const isExcel = /\.(xlsx|xls)$/i.test(file.name)
      if (!isExcel) {
        this.$message.error('仅支持.xlsx或.xls格式文件')
        return false
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$message.error('导入文件不能超过5MB')
        return false
      }
      this.itemImport.fileList = [file]
      this.itemImport.result = null
      return false
    },
    removeItemImportFile () {
      if (this.itemImport.importing) return false
      this.itemImport.fileList = []
      this.itemImport.result = null
      return true
    },
    submitItemImport () {
      if (!this.itemImport.fileList.length) return this.$message.warning('请先选择需要导入的Excel文件')
      const formData = new FormData()
      formData.append('file', this.itemImport.fileList[0])
      this.itemImport.importing = true
      return importPurchaseOrderItems(this.form.supplierId, this.form.warehouseId, formData).then(res => {
        const data = res.data || {}
        this.itemImport.result = data
        this.itemImport.fileList = []
        if (data.lines && data.lines.length) {
          this.applyImportedLines(data.lines)
        }
        if (data.failureCount) {
          this.$message.warning(`导入完成：成功 ${data.successCount} 行，失败 ${data.failureCount} 行`)
        } else if (data.successCount) {
          this.$message.success(`已成功导入 ${data.successCount} 行明细`)
        } else {
          this.$message.warning('未解析到有效明细行')
        }
      }).finally(() => { this.itemImport.importing = false })
    },
    applyImportedLines (lines) {
      ;(lines || []).forEach(row => {
        const units = this.normalizePurchaseUnits(row.purchaseUnits || [])
        const lastPriceByUnitId = {}
        ;(row.lastPurchasePrices || []).forEach(p => {
          if (p.unitId != null && p.purchasePrice != null) {
            lastPriceByUnitId[String(p.unitId)] = Number(p.purchasePrice)
          }
        })
        const line = {
          clientKey: `po-line-${Date.now()}-${++lineSeed}`,
          skuId: row.skuId,
          skuCode: row.skuCode,
          skuName: row.skuName,
          spuName: row.spuName,
          specName: row.specName || row.skuName,
          purchaseUnits: units,
          lastPriceByUnitId,
          qtyOrder: Number(row.qtyOrder),
          purchasePrice: this.roundPurchasePrice(row.purchasePrice),
          taxRate: row.taxRate == null ? 0 : Number(row.taxRate)
        }
        if (row.unitId) this.syncLineUnit(line, row.unitId, { convertPrice: false })
        this.form.items.push(line)
      })
    },
    openProductPicker () {
      if (!this.form.supplierId || !this.form.warehouseId) {
        this.$message.warning('请先选择供应商和收货仓库')
        return
      }
      this.productPicker.keyword = ''
      this.productPicker.selectedRowKeys = this.form.items.map(item => item.skuId).filter(Boolean)
      this.productPicker.visible = true
      this.loadSkuOptions()
    },
    loadSkuOptions () {
      this.productPicker.loading = true
      listPurchaseOrderSku(this.form.supplierId, this.form.warehouseId, this.productPicker.keyword).then(res => {
        this.productPicker.list = res.data || []
      }).finally(() => { this.productPicker.loading = false })
    },
    confirmProductPicker () {
      const selected = new Set(this.productPicker.selectedRowKeys.map(id => String(id)))
      const existing = new Set(this.form.items.map(item => String(item.skuId)))
      const added = this.productPicker.list.filter(sku => selected.has(String(sku.skuId)) && !existing.has(String(sku.skuId)))
      if (!added.length && !this.form.items.length) {
        this.$message.warning('请选择商品规格')
        return
      }
      added.forEach(sku => {
        const units = this.normalizePurchaseUnits(sku.purchaseUnits || [])
        const lastPriceByUnitId = this.buildLastPriceByUnitId(sku)
        const unit = this.pickDefaultPurchaseUnit(sku, units)
        const purchasePrice = this.resolveDefaultPurchaseLine(sku, unit, lastPriceByUnitId)
        const line = {
          clientKey: `po-line-${Date.now()}-${++lineSeed}`,
          skuId: sku.skuId,
          skuCode: sku.skuCode,
          skuName: sku.skuName,
          spuName: sku.spuName,
          specName: sku.skuName || sku.specName,
          purchaseUnits: units,
          lastPriceByUnitId,
          qtyOrder: 1,
          purchasePrice,
          taxRate: sku.taxRate == null ? 0 : Number(sku.taxRate)
        }
        if (unit) this.syncLineUnit(line, unit.unitId)
        this.form.items.push(line)
      })
      this.productPicker.visible = false
    },
    onUnitChange (line, unitId) {
      const uid = this.idStr(unitId)
      const cached = line.lastPriceByUnitId && line.lastPriceByUnitId[uid]
      if (cached != null) {
        this.syncLineUnit(line, uid, { convertPrice: false })
        line.purchasePrice = this.roundPurchasePrice(cached)
        return
      }
      if (!line.skuId) {
        this.syncLineUnit(line, uid, { convertPrice: true })
        return
      }
      listPurchaseOrderLastPrices([{ skuId: line.skuId, unitId: uid }]).then(res => {
        const rows = res.data || []
        const hit = rows.find(r => String(r.skuId) === String(line.skuId) && String(r.unitId) === uid)
        if (hit && hit.purchasePrice != null) {
          if (!line.lastPriceByUnitId) line.lastPriceByUnitId = {}
          line.lastPriceByUnitId[uid] = Number(hit.purchasePrice)
          this.syncLineUnit(line, uid, { convertPrice: false })
          line.purchasePrice = this.roundPurchasePrice(hit.purchasePrice)
          return
        }
        this.syncLineUnit(line, uid, { convertPrice: true })
      }).catch(() => this.syncLineUnit(line, uid, { convertPrice: true }))
    },
    lineAmount (item) {
      return (Number(item.qtyOrder || 0) * Number(item.purchasePrice || 0)).toFixed(2)
    },
    validateItems () {
      if (!this.form.items.length) return '请添加商品明细'
      if (this.form.items.some(item => !item.skuId)) return '请选择每一行商品'
      if (this.form.items.some(item => !item.unitId)) return '请选择每一行单位'
      if (this.form.items.some(item => !Number(item.qtyOrder))) return '数量不能为0'
      if (this.form.items.some(item => item.purchasePrice == null || Number(item.purchasePrice) < 0)) return '含税单价不能小于0'
      return ''
    },
    submit () {
      this.$refs.documentForm.validate(valid => {
        if (!valid) return
        const itemError = this.validateItems()
        if (itemError) return this.$message.warning(itemError)
        this.saving = true
        const payload = {
          id: this.form.id,
          orderDate: this.form.orderDate,
          supplierId: this.form.supplierId,
          warehouseId: this.form.warehouseId,
          purchaseUserId: this.form.purchaseUserId,
          deliveryTime: this.form.deliveryTime,
          partnerOrderNo: this.form.partnerOrderNo,
          deliveryAddress: this.form.deliveryAddress,
          deptName: this.form.deptName,
          attachmentUrl: this.form.attachmentUrl,
          remark: this.form.remark,
          items: this.form.items.map(item => ({
            skuId: item.skuId,
            unitId: item.unitId,
            qtyOrder: item.qtyOrder,
            purchasePrice: item.purchasePrice,
            taxRate: item.taxRate
          }))
        }
        savePurchaseOrder(payload).then(() => {
          this.$message.success('采购订单已保存')
          this.drawerVisible = false
          this.loadData()
        }).finally(() => { this.saving = false })
      })
    },
    confirmAudit (record) {
      this.$confirm({
        title: '审核采购订单？',
        content: '审核后可生成采购入库单。',
        onOk: () => auditPurchaseOrder(record.id).then(() => {
          this.$message.success('已审核')
          this.loadData()
        })
      })
    },
    confirmReverse (record) {
      this.$confirm({
        title: '反审核采购订单？',
        content: '仅在未生成入库单时允许反审核。',
        onOk: () => reverseAuditPurchaseOrder(record.id).then(() => {
          this.$message.success('已反审核')
          this.loadData()
        })
      })
    },
    confirmDelete (record) {
      this.$confirm({
        title: '删除未审核采购订单？',
        content: record.orderNo,
        okType: 'danger',
        onOk: () => deletePurchaseOrder(record.id).then(() => {
          this.$message.success('已删除')
          this.loadData()
        })
      })
    },
    goInbound (record) {
      const orderId = record && record.id != null ? String(record.id) : ''
      if (!orderId) return
      sessionStorage.setItem('purchaseInboundFromOrderId', orderId)
      this.$router.push({ path: '/erp/purchase-inbound', query: { fromOrderId: orderId } })
    },
    async doExport () {
      this.exporting = true
      try {
        const res = await exportPurchaseOrder({
          ...this.query,
          dateStart: this.dateRange[0],
          dateEnd: this.dateRange[1]
        })
        // request 拦截器对 blob 返回完整 axios response，文件在 res.data
        const blobData = res && res.data !== undefined ? res.data : res
        if (!(blobData instanceof Blob)) {
          this.$message.error('导出失败：响应格式异常')
          return
        }
        if (blobData.type && blobData.type.includes('application/json')) {
          const text = await blobData.text()
          let msg = '导出失败'
          try {
            const obj = JSON.parse(text)
            msg = obj.message || obj.msg || msg
          } catch (e) { /* ignore */ }
          this.$message.error(msg)
          return
        }
        const blob = new Blob([blobData], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `采购订单导出_${moment().format('YYYYMMDDHHmmss')}.xlsx`
        link.click()
        URL.revokeObjectURL(link.href)
      } catch (e) {
        this.$message.error((e && e.message) || '导出失败')
      } finally {
        this.exporting = false
      }
    },
    money (value) { return Number(value || 0).toFixed(2) }
  }
}
</script>

<style scoped>
.purchase-order-page{min-height:680px}
.purchase-search{display:flex;align-items:center;justify-content:space-between;gap:16px}
.search-fields{display:grid;grid-template-columns:200px 260px 140px 180px;gap:10px;flex:1}
.search-actions,.toolbar-actions{display:flex;gap:8px}
.row-actions{display:flex;flex-wrap:wrap;gap:10px}
.money{font-variant-numeric:tabular-nums}
.document-body{padding:16px 24px}
.document-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:0 14px}
.detail-table{min-width:1100px}
.picker-bar{display:flex;align-items:center;gap:10px;margin-bottom:12px}
.picker-bar .ant-input{max-width:320px}
.empty-row{text-align:center;color:#7b8798;padding:28px!important}
.amount-summary{display:flex;justify-content:flex-end;gap:18px;padding-top:12px;color:#7b8798}
.amount-summary b{color:#26364b;font-size:18px}
.detail-toolbar-actions{display:flex;flex-wrap:wrap;gap:8px;align-items:center}
.po-import-template-row{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:14px;padding:12px 14px;border:1px solid #e5e9f0;border-radius:6px;background:#f8fafc}
.po-import-template-row p{margin:4px 0 0;font-size:12px;color:#667085}
.po-import-result{margin-top:14px;padding:12px 14px;border-radius:6px;background:#f6ffed;border:1px solid #b7eb8f}
.po-import-result.has-errors{background:#fffbe6;border-color:#ffe58f}
.po-import-result-summary{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
.po-import-result-summary p{margin:2px 0 0;font-size:12px;color:#667085}
.po-import-error-table-wrap{margin-top:14px}
.po-import-error-title{margin:0 0 8px;font-size:13px;font-weight:600;color:#cf1322}
.po-import-error-table-wrap /deep/ .ant-table-body{max-height:280px}
@media(max-width:1100px){.purchase-search{flex-direction:column;align-items:stretch}.search-fields,.document-grid{grid-template-columns:1fr 1fr}}
</style>
